import React, { Fragment } from "react";
import { Row, Col, Table, InputGroup, Form, Spinner } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { Search } from "react-feather";

/**
 * Reusable DataTable component with pagination, search, and loading states.
 * @param {Object} props - The props object containing table data and functionalities.
 * @param {Array} props.columns - The array of column definitions for the table.
 * @param {Array} props.data - The array of data rows to be displayed in the table.
 * @param {number} props.totalRecords - The total number of records in the dataset.
 * @param {Object} props.page - The current page data containing PageNumber and PageSize.
 * @param {number} props.page.PageNumber - The current page number.
 * @param {number} props.page.PageSize - The number of records per page.
 * @param {Function} props.onPageChange - Function to handle page change.
 * @param {Function} props.searchRecords - Function to handle search functionality.
 * @param {boolean} props.loadingState - Flag indicating whether the table data is loading.
 * @param {string} props.searchPlaceholder - Placeholder text for the search input.
 * @param {Array} props.pageSizeOptions - Array of page size options (e.g., [10, 25, 50, 100]).
 * @param {boolean} props.showPageSizeOptions - Whether to show the page size options dropdown.
 * @param {boolean} props.showSearch - Whether to show the search input.
 * @param {boolean} props.showPagination - Whether to show the pagination controls.
 * @returns {JSX.Element} - Returns JSX for the data table component.
 */
const DataTable = ({
  columns,
  data,
  totalRecords,
  page,
  onPageChange,
  searchRecords,
  loadingState,
  searchPlaceholder = "Search...",
  pageSizeOptions = [25, 50, 75, 100],
  showPageSizeOptions = true,
  showSearch = true,
  showPagination = true,
  noDataMessageTitle = "No Data Found",
  noDataMessage = "",
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    state: { page },
    pageCount:
      totalRecords > page?.PageSize
        ? Math.ceil(totalRecords / page?.PageSize)
        : 1,
  });

  return (
    <Fragment>
      <Row className="mx-0 mb-3 align-items-center justify-content-between">
        {showPageSizeOptions && onPageChange && (
          <Col xs={12} md={6} lg={6} xl={6} className="mb-2 mb-md-0">
            <Form.Label className="me-2">Show</Form.Label>
            <Form.Select
              className="w-auto d-inline-block"
              onChange={(e) => onPageChange(1, parseInt(e.target.value))}
              size="sm"
              value={page?.PageSize}
            >
              {pageSizeOptions.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
            <Form.Label className="ms-2">entries</Form.Label>
          </Col>
        )}

        {showSearch && searchRecords && (
          <Col xs={12} md={6} lg={6} xl={3}>
            <InputGroup>
              <InputGroup.Text className="bg-transparent border-end-0">
                <Search size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="bg-transparent border-start-0"
                placeholder={searchPlaceholder}
                onKeyUp={(e) => {
                  if (e.target.value.length >= 2 || !e.target.value) {
                    searchRecords(e.target.value);
                  }
                }}
              />
            </InputGroup>
          </Col>
        )}
      </Row>

      <Table hover responsive className="mt-4">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}

          {totalRecords === 0 && !loadingState && (
            <tr>
              <td colSpan="6" className="text-center p-5">
                <div className="d-flex flex-column align-items-center text-muted">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    fill="currentColor"
                    className="mb-3"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 0a8 8 0 1 0 8 8A8 8 0 0 0 8 0zM4.285 12.433a.5.5 0 0 1-.57-.82 5.5 5.5 0 1 1 8.57 0 .5.5 0 0 1-.57.82A4.5 4.5 0 1 0 4.285 12.433zM8 5a.75.75 0 1 1-.75.75A.75.75 0 0 1 8 5zm-.25 2.75a.25.25 0 0 1 .25-.25h.5a.25.25 0 0 1 .25.25v3a.25.25 0 0 1-.25.25h-.5a.25.25 0 0 1-.25-.25v-3z" />
                  </svg>
                  <p className="fs-6 mb-1 fw-semibold">{noDataMessageTitle}</p>
                  <p className="text-muted mb-0" style={{ maxWidth: "500px" }}>
                    {noDataMessage ||
                      "No data available for the selected filters or search criteria."}
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* No Data Found */}

      {/* Loading Spinner */}
      {loadingState && (
        <div className="text-center mt-3">
          <Spinner animation="border" role="status" size="md">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Pagination */}
      {showPagination && totalRecords > page?.PageSize && (
        <div className="d-flex justify-content-center mt-4">
          <PaginationControl
            page={page?.PageNumber}
            between={3}
            total={totalRecords}
            limit={page?.PageSize}
            changePage={(pageNumber) =>
              onPageChange(pageNumber, page?.PageSize)
            }
            last
            ellipsis={2}
          />
        </div>
      )}
    </Fragment>
  );
};

export default DataTable;
